import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Snackbar,
  LinearProgress,
  Tooltip,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  useFinancialCycles,
  useCurrentFinancialCycle,
  useFinancialCycleSummary,
  useCloseFinancialCycle,
} from '../hooks/useFinancialCycles';

const FinancialCyclesPage: React.FC = () => {
  const navigate = useNavigate();
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [closeData, setCloseData] = useState({
    end_date: new Date().toISOString().split('T')[0],
    remarks: '',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({ open: false, message: '', severity: 'info' });

  const {
    data: cycles,
    isLoading,
    error,
    refetch,
    isFetching: cycleFetching,
  } = useFinancialCycles();
  const { data: currentCycle, isFetching: currentCycleFetching } = useCurrentFinancialCycle();
  const { data: summary, isFetching: summaryFetching } = useFinancialCycleSummary();
  const closeMutation = useCloseFinancialCycle();

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleOpenCloseDialog = () => {
    if (!currentCycle) {
      showSnackbar('No active cycle to close', 'warning');
      return;
    }
    setCloseData({ end_date: new Date().toISOString().split('T')[0], remarks: '' });
    setOpenCloseDialog(true);
  };

  const handleCloseCycle = async () => {
    if (!currentCycle || !closeData.end_date) {
      showSnackbar('Please select an end date', 'warning');
      return;
    }
    try {
      await closeMutation.mutateAsync({
        cycle_id: currentCycle.id,
        end_date: closeData.end_date,
        remarks: closeData.remarks || undefined,
      });
      showSnackbar('Financial cycle closed successfully', 'success');
      setOpenCloseDialog(false);
    } catch {
      showSnackbar('Failed to close financial cycle. Please try again.', 'error');
    }
  };

  const formatCurrency = (amount: number | string): string => {
    const num = typeof amount === 'number' ? amount : parseFloat(amount);
    return `GH₵ ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const getDuration = (startDate: string, endDate?: string): string => {
    const days = Math.floor(
      ((endDate ? new Date(endDate) : new Date()).getTime() - new Date(startDate).getTime()) /
        86400000
    );
    if (days < 30) return `${days} days`;
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  };

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Financial Cycles
            </Typography>
            <Typography variant="body2">
              {error instanceof Error ? error.message : 'An error occurred. Please try again.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const activeTotalForProgress = currentCycle
    ? Math.max(currentCycle.total_revenue, currentCycle.total_expenses, 1)
    : 1;
  const revenuePercent = currentCycle
    ? Math.min((currentCycle.total_revenue / activeTotalForProgress) * 100, 100)
    : 0;
  const expensesPercent = currentCycle
    ? Math.min((currentCycle.total_expenses / activeTotalForProgress) * 100, 100)
    : 0;

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Cycle Name',
      flex: 1.2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap={1.5} height="100%">
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: params.row.status === 'active' ? '#66bb6a' : '#616161',
              boxShadow: params.row.status === 'active' ? '0 0 6px #66bb6a' : 'none',
            }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <Typography
            variant="body2"
            sx={{
              color: params.value ? 'rgba(255,255,255,0.8)' : 'rgba(255,152,0,0.7)',
              fontStyle: params.value ? 'normal' : 'italic',
              fontSize: '0.85rem',
            }}
          >
            {params.value ? formatDate(params.value) : 'Ongoing'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      flex: 0.8,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (_value, row) => getDuration(row.start_date, row.end_date),
    },
    {
      field: 'total_revenue',
      headerName: 'Revenue',
      width: 155,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
          <Chip
            label={formatCurrency(params.value / 100)}
            size="small"
            sx={{
              bgcolor: 'rgba(102,187,106,0.1)',
              color: '#66bb6a',
              fontWeight: 700,
              border: '1px solid rgba(102,187,106,0.25)',
              minWidth: 125,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        </Box>
      ),
    },
    {
      field: 'total_expenses',
      headerName: 'Expenses',
      width: 155,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
          <Chip
            label={formatCurrency(params.value / 100)}
            size="small"
            sx={{
              bgcolor: 'rgba(244,67,54,0.1)',
              color: '#f44336',
              fontWeight: 700,
              border: '1px solid rgba(244,67,54,0.25)',
              minWidth: 125,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        </Box>
      ),
    },
    {
      field: 'net_profit',
      headerName: 'Net Profit',
      width: 155,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const profit = params.value / 100;
        const pos = profit >= 0;
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            width="100%"
          >
            <Chip
              label={formatCurrency(profit)}
              size="small"
              sx={{
                bgcolor: pos ? 'rgba(102,187,106,0.1)' : 'rgba(244,67,54,0.1)',
                color: pos ? '#66bb6a' : '#f44336',
                fontWeight: 700,
                border: `1px solid ${pos ? 'rgba(102,187,106,0.25)' : 'rgba(244,67,54,0.25)'}`,
                minWidth: 125,
                '& .MuiChip-label': { px: 1.5 },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
          <Chip
            label={params.value === 'active' ? 'Active' : 'Closed'}
            size="small"
            icon={params.value === 'active' ? <AccessTimeIcon /> : <CheckCircleIcon />}
            sx={{
              fontWeight: 600,
              minWidth: 88,
              bgcolor: params.value === 'active' ? 'rgba(102,187,106,0.1)' : 'rgba(97,97,97,0.1)',
              color: params.value === 'active' ? '#66bb6a' : '#9e9e9e',
              border: `1px solid ${params.value === 'active' ? 'rgba(102,187,106,0.3)' : 'rgba(97,97,97,0.25)'}`,
              '& .MuiChip-icon': { color: 'inherit', fontSize: '0.9rem' },
            }}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Loading bar — fixed at top */}
      {(cycleFetching || currentCycleFetching || summaryFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Header — slim 2-line */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 2 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
            Financial Cycles
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Track revenue, expenses, and profitability across farm cycles
          </Typography>
        </Box>
        {currentCycle && (
          <Button
            variant="contained"
            size="small"
            startIcon={<LockIcon />}
            onClick={handleOpenCloseDialog}
            sx={{
              background: 'linear-gradient(135deg,#d32f2f,#f44336)',
              color: '#fff',
              px: 2,
              '&:hover': { background: 'linear-gradient(135deg,#b71c1c,#d32f2f)' },
            }}
          >
            Close Cycle
          </Button>
        )}
      </Box>

      {/* Active cycle strip */}
      {currentCycle && (
        <Paper
          sx={{
            mb: 1.5,
            px: 0,
            py: 0,
            background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            border: '1px solid rgba(102,187,106,0.25)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Left: pulsing dot + LIVE chip + cycle name */}
            <Box
              sx={{
                py: 1.25,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#66bb6a',
                  boxShadow: '0 0 6px #66bb6a',
                  flexShrink: 0,
                  animation: 'fcPulse 2s infinite',
                  '@keyframes fcPulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.55, transform: 'scale(0.8)' },
                  },
                }}
              />
              <Chip
                label="LIVE"
                size="small"
                sx={{
                  bgcolor: 'rgba(102,187,106,0.12)',
                  color: '#66bb6a',
                  border: '1px solid rgba(102,187,106,0.4)',
                  fontWeight: 700,
                  fontSize: '0.62rem',
                  letterSpacing: '1px',
                  height: 20,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: '#fff', fontSize: '0.82rem', whiteSpace: 'nowrap' }}
              >
                {currentCycle.name}
              </Typography>
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: 'rgba(102,187,106,0.15)' }}
            />

            {/* Metrics */}
            {[
              {
                label: 'START DATE',
                value: formatDate(currentCycle.start_date),
                color: '#fff',
              },
              {
                label: 'DURATION',
                value: getDuration(currentCycle.start_date),
                color: '#fff',
              },
              {
                label: 'REVENUE',
                value: formatCurrency(currentCycle.total_revenue / 100),
                color: '#66bb6a',
              },
              {
                label: 'NET PROFIT',
                value: formatCurrency(currentCycle.net_profit / 100),
                color: currentCycle.net_profit >= 0 ? '#66bb6a' : '#f44336',
              },
            ].map((item, i, arr) => (
              <React.Fragment key={item.label}>
                <Box sx={{ flex: 1, py: 1.25, px: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,152,0,0.7)',
                        fontSize: '0.6rem',
                        display: 'block',
                        letterSpacing: '0.5px',
                        fontWeight: 600,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      fontWeight={700}
                      sx={{ color: item.color, lineHeight: 1.1, fontSize: '0.92rem' }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
                {i < arr.length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: 'rgba(102,187,106,0.12)' }}
                  />
                )}
              </React.Fragment>
            ))}

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: 'rgba(102,187,106,0.15)' }}
            />

            {/* Revenue vs Expenses progress section */}
            <Box
              sx={{
                py: 1.25,
                px: 2,
                minWidth: 160,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}
                >
                  Revenue vs Expenses
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}
                >
                  {currentCycle.total_expenses > 0
                    ? `${((currentCycle.total_revenue / currentCycle.total_expenses) * 100).toFixed(0)}% ROI`
                    : '— ROI'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  height: 5,
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'rgba(255,255,255,0.05)',
                }}
              >
                <Box
                  sx={{
                    width: `${revenuePercent}%`,
                    bgcolor: '#66bb6a',
                    borderRadius: '3px 0 0 3px',
                    transition: 'width 0.6s ease',
                  }}
                />
                <Box
                  sx={{
                    width: `${expensesPercent}%`,
                    bgcolor: '#f44336',
                    transition: 'width 0.6s ease',
                  }}
                />
              </Box>
              <Box display="flex" gap={2} mt={0.5}>
                {[
                  { color: '#66bb6a', label: 'Revenue' },
                  { color: '#f44336', label: 'Expenses' },
                ].map((item) => (
                  <Box key={item.label} display="flex" alignItems="center" gap={0.5}>
                    <Box sx={{ width: 6, height: 6, borderRadius: 1, bgcolor: item.color }} />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Summary stats strip */}
      {summary && (
        <Paper
          sx={{
            mb: 1.5,
            px: 0,
            py: 0,
            background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            border: '1px solid rgba(255,152,0,0.12)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {[
              {
                label: 'TOTAL CYCLES',
                value: String(summary.total_cycles || 0),
                sub: `${summary.active_cycles || 0} active`,
                color: '#2196f3',
              },
              {
                label: 'TOTAL REVENUE',
                value: formatCurrency((summary.total_revenue || 0) / 100),
                sub: 'All cycles',
                color: '#66bb6a',
              },
              {
                label: 'TOTAL EXPENSES',
                value: formatCurrency((summary.total_expenses || 0) / 100),
                sub: 'All cycles',
                color: '#f44336',
              },
              {
                label: 'NET PROFIT',
                value: formatCurrency((summary.total_profit || 0) / 100),
                sub: `${(summary.profit_margin || 0).toFixed(1)}% margin`,
                color: (summary.total_profit || 0) >= 0 ? '#66bb6a' : '#f44336',
                trend: (summary.profit_margin || 0) >= 0 ? 'up' : 'down',
              },
            ].map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <Box sx={{ flex: 1, py: 1.25, px: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem', display: 'block' }}
                    >
                      {s.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography
                        fontWeight={700}
                        sx={{ color: s.color, lineHeight: 1.1, fontSize: '0.92rem' }}
                      >
                        {s.value}
                      </Typography>
                      {s.sub && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.62rem' }}
                        >
                          {s.sub}
                        </Typography>
                      )}
                    </Box>
                    {s.trend && (
                      <Box display="flex" alignItems="center" gap={0.25} mt={0.25}>
                        {s.trend === 'up' ? (
                          <TrendingUpIcon sx={{ fontSize: '0.75rem', color: '#66bb6a' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: '0.75rem', color: '#f44336' }} />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                {i < arr.length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: 'rgba(255,152,0,0.08)' }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}

      {/* Table Paper */}
      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(255,152,0,0.15)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Toolbar row */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            flexShrink: 0,
          }}
        >
          <TimelineIcon sx={{ color: '#ff9800', fontSize: '1.1rem' }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff' }}>
            Cycles
          </Typography>
          {cycles && (
            <Chip
              label={cycles.length}
              size="small"
              sx={{
                bgcolor: 'rgba(255,152,0,0.12)',
                color: '#ff9800',
                fontWeight: 700,
                height: 20,
                fontSize: '0.72rem',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          <Box flex={1} />
          <Tooltip title="Click a row to view cycle details">
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}
            >
              Click a row to view details
            </Typography>
          </Tooltip>
        </Box>

        {/* DataGrid */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            rows={cycles || []}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/financial/cycles/${params.id}`)}
            loading={isLoading}
            rowHeight={44}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'start_date', sort: 'desc' }] },
            }}
            sx={{
              border: 'none',
              height: '100%',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(255,152,0,0.08)',
                px: 2,
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'rgba(255,152,0,0.07)',
                borderBottom: '1px solid rgba(255,152,0,0.18)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                fontSize: '0.72rem',
                letterSpacing: '0.8px',
                color: 'rgba(255,152,0,0.85)',
                textTransform: 'uppercase',
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,152,0,0.05)' },
                '&.Mui-selected': {
                  bgcolor: 'rgba(255,152,0,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,152,0,0.13)' },
                },
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid rgba(255,152,0,0.18)',
                bgcolor: 'rgba(255,152,0,0.04)',
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
            }}
          />
        </Box>
      </Paper>

      {/* Close Cycle Dialog */}
      <Dialog
        open={openCloseDialog}
        onClose={() => setOpenCloseDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(244,67,54,0.35)', borderRadius: 2 } } }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(244,67,54,0.15)',
            pb: 2,
            background:
              'linear-gradient(135deg, rgba(211,47,47,0.08) 0%, rgba(244,67,54,0.03) 100%)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              sx={{ bgcolor: 'rgba(244,67,54,0.12)', color: '#f44336', width: 40, height: 40 }}
            >
              <LockIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#f44336' }}>
                Close Financial Cycle
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                {currentCycle?.name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
            {currentCycle && (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,152,0,0.12)',
                  borderRadius: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,152,0,0.8)', fontWeight: 600, letterSpacing: '0.5px' }}
                >
                  CYCLE SUMMARY
                </Typography>
                <Divider sx={{ my: 1, borderColor: 'rgba(255,152,0,0.1)' }} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}
                    >
                      Revenue
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ color: '#66bb6a' }}>
                      {formatCurrency(currentCycle.total_revenue / 100)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}
                    >
                      Expenses
                    </Typography>
                    <Typography variant="body1" fontWeight={700} sx={{ color: '#f44336' }}>
                      {formatCurrency(currentCycle.total_expenses / 100)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ mb: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}
                    >
                      Net Profit
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: currentCycle.net_profit >= 0 ? '#66bb6a' : '#f44336' }}
                    >
                      {formatCurrency(currentCycle.net_profit / 100)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <TextField
              label="End Date"
              name="end_date"
              type="date"
              value={closeData.end_date}
              onChange={(e) => setCloseData({ ...closeData, end_date: e.target.value })}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="Remarks (Optional)"
              name="remarks"
              value={closeData.remarks}
              onChange={(e) => setCloseData({ ...closeData, remarks: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Add any notes about this cycle..."
            />

            <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
              Closing this cycle will finalize all financial records. This action cannot be undone.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            borderTop: '1px solid rgba(244,67,54,0.12)',
            gap: 1,
          }}
        >
          <Button
            onClick={() => setOpenCloseDialog(false)}
            variant="outlined"
            sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.2)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCloseCycle}
            variant="contained"
            disabled={closeMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
              color: '#ffffff',
              '&:hover': { background: 'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)' },
            }}
          >
            {closeMutation.isPending ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              'Finalize & Close'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialCyclesPage;
